import { defineStore } from "pinia";
import api from "@/services/api";
import { obs } from "@/utils/observability";
import { getSelectedCompanyId } from "@/utils/companyContext";
import type{ Company, CompanyFacilityTypes, MetaDataKey, OperationYear, Sector, SubSector, Unit } from "@/types";


type State = {
  sectors: Sector[];
  subSectors: SubSector[];
  isLoading: boolean;
  company: Company;
  companyFacilityTypes: CompanyFacilityTypes[];
  totalFacilityCount: number;
  operationYears: OperationYear[];
  companyOnboardingStatus: string;
  isFacilityError: boolean;
  facilityErrors: string[];
  facilityErrorIndex: number | null;
  isError: boolean;
  companyMetaDataKeys: MetaDataKey[];
  companyId: string | null;
  units: Unit[];
};


// Concurrent-load dedupe: route middleware, layouts and views fire the same company-scoped
// fetches together on page load; share the in-flight request instead of hitting the API twice.
let companyInFlight: { id: string; promise: Promise<void> } | null = null;
let operationYearsInFlight: { id: string; promise: Promise<void> } | null = null;

export const useCompanyStore = defineStore({
  id: "company",
  state: (): State => ({
    sectors: [] as Sector[],
    subSectors: [] as SubSector[],
    isLoading: false,
    company: {} as Company,
    companyFacilityTypes: [] as CompanyFacilityTypes[],
    totalFacilityCount: 0,
    operationYears: [] as OperationYear[],
    companyOnboardingStatus: "" as string,
    isFacilityError: false,
    facilityErrors: [],
    facilityErrorIndex: null,
    isError: false,
    companyMetaDataKeys: [] as MetaDataKey[],
    companyId: localStorage.getItem("companyId") || null,
    units: [] as Unit[] 
  }),
  actions: {
    async getCompany(companyId: string | null = null): Promise<void> {
      if (!companyId) {
        companyId = getSelectedCompanyId();
      }
      // No company selected yet — skip to avoid hitting `company/null`.
      if (!companyId || companyId === "null") {
        obs.breadcrumb("company.getCompany.skipped", { reason: "no-company" });
        return;
      }
      // Page loads fire this concurrently (route middleware + layout): share the
      // in-flight request instead of fetching twice. Sequential calls still refetch.
      if (companyInFlight && companyInFlight.id === companyId) {
        return companyInFlight.promise;
      }
      this.isLoading = true;

      const promise = (async () => {
        try {
          const { data } = await api.get(`/company/${companyId}`);
          this.company = data;
          this.companyOnboardingStatus = data.onboardingStatus;
          this.isLoading = false;
        } catch (e) {
        } finally {
          companyInFlight = null;
        }
      })();
      companyInFlight = { id: companyId, promise };
      return promise;
    },
    /**
     * Terminal onboarding transition for the matrix-based emission step. The backend
     * flips onboardingStatus to COMPLETE synchronously; state is refreshed from the
     * response. Throws on failure so the caller can stay on the step.
     */
    async completeOnboarding(): Promise<void> {
      const companyId = getSelectedCompanyId();
      if (!companyId || companyId === "null") return;
      const { data } = await api.post(`/company/${companyId}/onboarding/complete`);
      this.company = data;
      this.companyOnboardingStatus = data.onboardingStatus;
    },
    async updateCompany(id: string, payload: object) {
      this.isLoading = true;
      await api.put(`/company/${id}`, payload).then((response) => {
        this.getCompany();
        this.isLoading = false;
      });
    },
    async getCompanyFacilityTypes() {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      await api.get(`/company/${companyId}/facility-types`).then((response) => {
        const { data } = response;
        this.companyFacilityTypes = data;
        this.totalFacilityCount = this.companyFacilityTypes.reduce(
          (acc, obj) => acc + obj.facilityCount,
          0
        );
        this.isLoading = false;
      });
    },
    updateCompanyFacilityCount(facility: CompanyFacilityTypes, count: number) {
      this.companyFacilityTypes = this.companyFacilityTypes.map((i) => {
        i.facilityCount =
          i.typeName === facility.typeName ? count : i.facilityCount;
        return i;
      });

      this.totalFacilityCount = this.companyFacilityTypes.reduce(
        (acc, obj) => acc + obj.facilityCount,
        0
      );
    },
    async updateCompanyFacilityTypes() {
      this.isLoading = true;
      this.isError = false;

      const payload: CompanyFacilityTypes[] = [];

      this.companyFacilityTypes.forEach((f: CompanyFacilityTypes) => {
        if (f.facilityType !== "CUSTOM") {
          payload.push(f);
        }
        if (f.facilityType === "CUSTOM" && f.typeName !== "") {
          payload.push(f);
        }
      });

      const uniqueValues =
        this.companyFacilityTypes.map((v) => v.typeName).length >
        new Set(this.companyFacilityTypes.map((v) => v.typeName)).size;

      if (uniqueValues) {
        return;
      }

      try {
        const { data } = await api.put(
          `/company/${this.company.id}/facility-types`,
          payload
        );
        this.companyFacilityTypes = data;
        this.isLoading = false;
        this.isError = false;
      } catch (e) {
        this.isLoading = false;
        this.isError = true;
      }
    },
    addCustomFacilityType(typeOrder: number) {
      const companyId = localStorage.getItem("companyId");
      this.companyFacilityTypes.push({
        facilityType: "CUSTOM",
        logicalCompanyId: `${companyId}`,
        facilityCount: 0,
        typeNameLabel: "",
        typeName: "",
        typeOrder,
      });
    },
    setCustomFacilityTypeName(
      name: string,
      index: number,
      existCount: number
    ): boolean {
      if (!name) {
        return false;
      }

      const isContain: CompanyFacilityTypes | undefined =
        this.companyFacilityTypes.find(
          (f) => f.typeName === name && f.typeOrder != index
        );

      this.companyFacilityTypes = this.companyFacilityTypes.map((facility) => {
        facility.typeNameLabel =
          facility.typeOrder === index ? name : facility.typeNameLabel;
        facility.typeName =
          facility.typeOrder === index ? name : facility.typeName;
        facility.facilityCount =
          facility.typeOrder === index ? 1 : facility.facilityCount;
        facility.isError = !!(facility.typeOrder === index && isContain);
        return facility;
      });

      return existCount === 0;
    },

    async deleteCustomFacilityType(typeName: string) {
      const index = this.companyFacilityTypes.findIndex((item) => item.typeNameLabel === typeName);

      if (index !== -1) {
        this.companyFacilityTypes.splice(index, 1);
        this.companyFacilityTypes = [...this.companyFacilityTypes];
      }

    },
    async getSectors() {
      this.isLoading = true;
      await api.get(`/sector`).then((response) => {
        const { data } = response;
        this.sectors = data;
        this.isLoading = false;
      });
    },
    async getSubSectors(id: string) {
      this.isLoading = true;
      await api.get(`/sector/${id}/sub-sector`).then((response) => {
        const { data } = response;
        this.subSectors = data;
        this.isLoading = false;
      });
    },
    async getUnits() {
      this.isLoading = true;
      await api.get("/unit?intensityType=PRODUCTION").then((response) => {
        const { data } = response;
        this.units = data;
        this.isLoading = false;
      });
    },
    async fetchOperationYears() {
      const companyId = localStorage.getItem("companyId");
      // No company selected yet (e.g. super admin before CompanySelection):
      // skip company-scoped call so we don't hit `company/null` or throw.
      if (!companyId || companyId === "null") {
        obs.breadcrumb("operation-years.skipped", { reason: "no-company" });
        return;
      }
      if (operationYearsInFlight && operationYearsInFlight.id === companyId) {
        return operationYearsInFlight.promise;
      }
      const promise = this._fetchOperationYears(companyId);
      operationYearsInFlight = { id: companyId, promise };
      try {
        return await promise;
      } finally {
        operationYearsInFlight = null;
      }
    },
    async _fetchOperationYears(companyId: string): Promise<void> {
      try {
        const { data } = await api.get(`company/${companyId}/operation-years`);
        // Normalize: API may return `year` as a number, but the model
        // (calculationYear), override fields, and lookups all assume string.
        // A type mismatch makes PrimeVue Select option-value="year" never match.
        this.operationYears = (data ?? []).map((oy: OperationYear) => ({
          ...oy,
          year: String(oy.year),
        }));
        obs.breadcrumb("operation-years.fetched", {
          count: this.operationYears.length,
          yearTypes: [...new Set((data ?? []).map((d: any) => typeof d.year))],
        });
      } catch (e) {
        obs.captureError(e, "fetchOperationYears", { companyId });
        throw e;
      }
    },
    async fetchCompanyMetaData () {
      try {
          const { data } = await api.get(`company/${localStorage.getItem('companyId')}/metadata`)
          this.companyMetaDataKeys = data
      } catch (e) {
          this.companyMetaDataKeys = []
      }
    },
    async updateIntensityUnit(unitId: string) {
      this.isLoading = true;
      try {
        const payload = { id: unitId };
        await api.put(`/company/${localStorage.getItem('companyId')}/intensity-unit`, payload);
        await this.getCompany();
        this.isLoading = false;
        return true;
      } catch (e) {
        this.isLoading = false;
        return false;
      }
    }    
  },
  getters: {
    getFacilityType(state) {
      return state.companyFacilityTypes.sort((a, b) => {
        return a.typeOrder - b.typeOrder;
      });
    },
    getCompanyFacilities({ companyFacilityTypes }): CompanyFacilityTypes[] {
      return companyFacilityTypes.filter(
        (facility) => facility.facilityCount !== 0
      );
    },
    getCompanyInfo({ company }): Company | null {
      if (!company) {
        return company;
      } else {
        return null;
      }
    },
    getOperationYears({ operationYears }): OperationYear[] {
      return operationYears
    },
    companyBaseYear({ operationYears }): string {
      const year = operationYears.find((y) => y.isBaseYear);

      if (year) {
        return year.year;
      }

      return "";
    },
    isErrorForCompanyFacilityTypes({ companyFacilityTypes }): boolean {
      const isError = companyFacilityTypes.find((f) => f.isError);
      return isError != undefined;
    },
    getIsLoading({ isLoading }): boolean {
      return isLoading;
    },
  },
});
